import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Clock, Instagram, Facebook, Youtube, Linkedin, MessageCircle } from "lucide-react";
import { useSettings, waLink, telLink } from "../context/SettingsContext";

export default function Footer() {
  const { settings } = useSettings();
  const socials = [
    { url: settings.instagram, Icon: Instagram },
    { url: settings.facebook, Icon: Facebook },
    { url: settings.youtube, Icon: Youtube },
    { url: settings.linkedin, Icon: Linkedin },
  ].filter((s) => s.url);

  return (
    <footer className="bg-ink text-white/80 pt-16 pb-28 lg:pb-12" data-testid="site-footer">
      <div className="container-x grid gap-12 lg:grid-cols-4">
        <div className="lg:col-span-1">
          <img src={settings.logo_url || "/logo.png"} alt="GNONS" className="h-12 w-auto mb-5 bg-white rounded-lg p-2" />
          <p className="text-sm leading-relaxed text-white/60 max-w-xs">{settings.tagline || "Architecture | Interior | Construction"} — smart design and quality construction across Indore.</p>
          {socials.length > 0 && (
            <div className="flex gap-3 mt-6">
              {socials.map(({ url, Icon }, i) => (
                <a key={i} href={url} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center hover:bg-gold hover:border-gold transition-colors">
                  <Icon size={16} />
                </a>
              ))}
            </div>
          )}
        </div>

        <div className="border-t border-white/10 pt-8 lg:border-t-0 lg:pt-0 lg:border-l lg:border-white/10 lg:pl-10">
          <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">Explore</h4>
          <ul className="space-y-2.5 text-sm">
            {[["Home", "/"], ["About Us", "/about"], ["Services", "/services"], ["Projects", "/projects"], ["Blog", "/blog"], ["Cost Calculator", "/calculator"]].map(([l, to]) => (
              <li key={to}><Link to={to} className="text-white/60 hover:text-gold transition-colors">{l}</Link></li>
            ))}
          </ul>
        </div>

        <div className="border-t border-white/10 pt-8 lg:border-t-0 lg:pt-0 lg:border-l lg:border-white/10 lg:pl-10">
          <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">Services</h4>
          <ul className="space-y-2.5 text-sm">
            {["Architectural Design", "3D Elevation", "Interior Design", "Construction", "Turnkey Projects", "Renovation"].map((s) => (
              <li key={s}><Link to="/services" className="text-white/60 hover:text-gold transition-colors">{s}</Link></li>
            ))}
          </ul>
        </div>

        <div className="border-t border-white/10 pt-8 lg:border-t-0 lg:pt-0 lg:border-l lg:border-white/10 lg:pl-10">
          <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">Get in Touch</h4>
          <ul className="space-y-3.5 text-sm">
            <li className="flex gap-3"><MapPin size={17} className="text-gold shrink-0 mt-0.5" /><span className="text-white/60">{settings.address}</span></li>
            <li className="flex gap-3"><Phone size={17} className="text-gold shrink-0" /><a href={telLink(settings.phone)} className="text-white/60 hover:text-gold">{settings.phone}</a></li>
            <li className="flex gap-3"><MessageCircle size={17} className="text-gold shrink-0" /><a href={waLink(settings.whatsapp, `Hello ${settings.company_name || "GNONS"}, I would like to discuss my project.`)} target="_blank" rel="noreferrer" className="text-white/60 hover:text-gold">WhatsApp Us</a></li>
            <li className="flex gap-3"><Mail size={17} className="text-gold shrink-0" /><a href={`mailto:${settings.email}`} className="text-white/60 hover:text-gold break-all">{settings.email}</a></li>
            <li className="flex gap-3"><Clock size={17} className="text-gold shrink-0" /><span className="text-white/60">{settings.business_hours}</span></li>
          </ul>
        </div>
      </div>

      <div className="container-x mt-12 pt-6 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-white/40">
        <p>© {new Date().getFullYear()} {settings.company_name || "GNONS Design & Construction"}. All rights reserved.</p>
        <div className="flex gap-5">
          <Link to="/admin" className="hover:text-gold">Admin</Link>
          <a href={settings.website} target="_blank" rel="noreferrer" className="hover:text-gold">{(settings.website || "gnons.in").replace(/^https?:\/\//, "")}</a>
        </div>
      </div>
    </footer>
  );
}
